import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";

const API_URL = process.env.REACT_APP_API_URL;

const Role = () => {
  const { canCreate, canUpdate, canDelete } = useModuleAccess(
    "role_management",
  );
  const savedFilters = JSON.parse(
    sessionStorage.getItem("roleFilters") || "{}",
  );
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteRoleId, setDeleteRoleId] = useState(null);
  const [deleteRoleName, setDeleteRoleName] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const fetchRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = {
        action: "get-role",
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
      };

      const trimmedSearch = activeSearchQuery?.trim();
      if (trimmedSearch) payload.search = trimmedSearch;

      if (sortBy) {
        payload.sortBy = sortBy;
        payload.sortOrder = sortOrder;
      }

      const res = await axios.post(API_URL, payload);
      let data = res.data;
      if (typeof data === "string") data = JSON.parse(data);

      if (data.success) {
        setRoles(data.data || []);
        setFilteredRoles(data.data || []);
        setTotalCount(data.pagination?.total || data.data?.length || 0);
      } else {
        toast.error(data.message || "Failed to load roles");
        setRoles([]);
        setFilteredRoles([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Fetch roles error:", err);
      toast.error("Error fetching roles");
      setRoles([]);
      setFilteredRoles([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, activeSearchQuery, sortBy, sortOrder]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = roles.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };

  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      currentPage,
    };

    sessionStorage.setItem("roleFilters", JSON.stringify(filters));
  }, [searchQuery, activeSearchQuery, currentPage, sortBy, sortOrder]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push("ellipsis", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1, "ellipsis");
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1, "ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("ellipsis", totalPages);
      }
    }
    return pages;
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);

    if (!val.trim() && activeSearchQuery) {
      setActiveSearchQuery("");
      if (currentPage !== 1) setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    const trimmed = searchQuery?.trim() || "";
    setActiveSearchQuery(trimmed);
    if (currentPage !== 1) setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleSort = (column) => {
    const columnMap = {
      sno: "createdAt",
      role: "role",
      status: "status",
    };
    const apiField = columnMap[column] || column;

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("createdAt");
        setSortOrder("desc");
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const columnMap = {
      sno: "createdAt",
      role: "role",
      status: "status",
    };
    const apiField = columnMap[column] || column;

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em" }}
        />
      );
    }
    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em" }}
        />
      );
    }
    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em" }}
      />
    );
  };

  const handleDeleteClick = (id, roleName) => {
    setDeleteRoleId(id);
    setDeleteRoleName(roleName);
  };

  const handleRolesPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleDelete = async () => {
    if (!deleteRoleId) return;

    const currentUserId = localStorage.getItem("id");
    if (!currentUserId) {
      toast.error("Session expired. Please login again.");
      navigate("/");
      return;
    }

    setIsDeleting(true);

    try {
      const payload = {
        action: "delete-role",
        id: deleteRoleId,
        updatedBy: currentUserId,
      };

      const res = await axios.post(API_URL, payload);
      let data = res.data;
      if (typeof data === "string") data = JSON.parse(data);

      if (data.success) {
        toast.success("Role deactivated");

        if (deleteRoleName) {
          try {
            await axios.post(API_URL, {
              action: "delete-role-access",
              role: String(deleteRoleName).toLowerCase(),
              updatedBy: currentUserId,
              currentUserId,
            });
          } catch (accessErr) {
            console.error("Delete role access error:", accessErr);
          }
        }

        if (window.$) window.$("#modaldelete").modal("hide");

        setDeleteRoleId(null);
        setDeleteRoleName("");

        if (roles.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchRoles();
        }
      } else {
        toast.error(data.message || "Failed to delete role");
      }
    } catch (err) {
      console.error("Delete role error:", err);
      toast.error("Error deleting role");
    } finally {
      setIsDeleting(false);
    }
  };

  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "-";

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Roles</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <Link to="/roles/add" className="btn btn-blue">
                  Create Role
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="form-group mb-0">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search roles..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                        />
                        <div className="input-group-append">
                          <div
                            className="input-group-text crsr_pntr"
                            onClick={handleSearchTrigger}
                          >
                            <i className="fa-solid fa-search" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6 text-right">
                    {activeSearchQuery && (
                      <span className="ml-2 text-muted">
                        (Showing: {filteredRoles.length})
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : roles.length === 0 ? (
                <div className="nocontent_div text-center p-4">
                  There are no roles yet.
                  {canCreate && (
                    <div className="mt-3">
                      <Link to="/roles/add" className="btn btn-blue">
                        Create Role
                      </Link>
                    </div>
                  )}
                </div>
              ) : filteredRoles.length === 0 && activeSearchQuery ? (
                <div className="nocontent_div text-center p-4">
                  No roles found matching your search.
                  <div className="mt-3">
                    <button
                      className="btn btn-blue"
                      onClick={() => {
                        setSearchQuery("");
                        setActiveSearchQuery("");
                        setCurrentPage(1);
                      }}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table className="table table-striped table-bordered txt_lft">
                      <thead>
                        <tr>
                          <th
                            className="crsr_pntr"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              S.No
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("role")}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Role {getSortIcon("role")}
                            </span>
                          </th>
                          <th
                            className="crsr_pntr"
                            onClick={() => handleSort("status")}
                            style={{ whiteSpace: "nowrap" }}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Status {getSortIcon("status")}
                            </span>
                          </th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRoles.map((role, index) => (
                          <tr key={role.id}>
                            <td>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td className="text-capitalize">{role.role}</td>
                            <td>
                              <span
                                className={`fwt_600 ${
                                  role.status === "active" ? "f_green" : "f_red"
                                }`}
                              >
                                {capitalize(role.status)}
                              </span>
                            </td>
                            <td>
                              <div align="center">
                                {role.status === "active" && (
                                  <>
                                    {canUpdate && (
                                      <Link
                                        to={`/roles/edit/${role.id}`}
                                        className="crsr_pntr mr-2"
                                        title="Edit"
                                      >
                                        <i className="fa-solid fa-pen" />
                                      </Link>
                                    )}

                                    {canDelete && (
                                      <a
                                        href="#modaldelete"
                                        className="crsr_pntr action-icon-link"
                                        data-toggle="modal"
                                        data-target="#modaldelete"
                                        onClick={() =>
                                          handleDeleteClick(role.id, role.role)
                                        }
                                        title="Delete"
                                      >
                                        <i className="fa-solid fa-trash-can" />
                                      </a>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {!isLoading && roles.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleRolesPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="modaldelete"
        tabIndex={-1}
        aria-labelledby="modaldeleteLabel"
        aria-hidden="true"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modaldeleteLabel">
                Deactivate Role
              </h5>
              <button type="button" className="close" data-dismiss="modal">
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              <div className="w-100 d-inline-block text-center">
                Are you sure you want to deactivate <br />
                <span className="fwt_600">{deleteRoleName}</span>?
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                data-dismiss="modal"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deactivating..." : "Yes, deactivate"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Role;
